/**
 * Markdown-it plugin for handling wikilinks
 * @fileoverview Processes [[wikilink]] syntax and converts to regular links.
 * Obsidian convention: [[target|label]] — `label` is the display text,
 * `target` is the href target (used verbatim, no slugification).
 *
 *   [[Page]]        → <a href="/Page.html" class="cm-wikilink-preview">Page</a>
 *   [[Page|label]]  → <a href="/Page.html" class="cm-wikilink-preview">label</a>
 *
 * With `wikilinksSearchPrefix` set, the href becomes a search query instead
 * (`#&<prefix>=<target>`, URL-encoded). Images: ![[src]] / ![[alt|src]].
 */

/**
 * Plugin function for markdown-it to handle wikilinks
 * @param {Object} md - The markdown-it instance
 * @param {Object} options - Plugin options
 * @param {string} [options.wikilinksSearchPrefix] - Prefix for search URLs
 */
function wikilinksPlugin(md, options = {}) {
  const wikilinkRegex = /!\[\[([^|\]]+)(?:\|([^\]]+))?\]\]|\[\[([^|\]]+)(?:\|([^\]]+))?\]\]/g;
  const imageRegex = /\.(jpg|jpeg|webp|png|gif|svg|mp4)$/i;

  /**
   * Checks if a URL is likely an image
   * @param {string} url - The URL to check
   * @return {boolean} True if the URL appears to be an image
   */
  function isImageUrl(url) {
    return imageRegex.test(url) ||
      url.includes('placehold') ||
      url.includes('placeholder') ||
      url.startsWith('data:image') ||
      url.startsWith('http') ||
      url.startsWith('/');
  }

  /**
   * Escapes HTML special characters in a string
   * @param {string} [str=''] - String to escape
   * @return {string} HTML-escaped string
   */
  function escapeHtml(str = '') {
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  /**
   * Rule function for processing wikilinks in markdown tokens
   * @param {Object} state - The markdown-it state object
   */
  function wikilinkRule(state) {
    const tokens = state.tokens;

    for (let i = 0; i < tokens.length; i++) {
      if (tokens[i].type === 'inline' && tokens[i].content.match(wikilinkRegex)) {
        const inlineTokens = [];
        let lastIndex = 0;
        let match;

        while ((match = wikilinkRegex.exec(tokens[i].content)) !== null) {
          const isImage = match[0].startsWith('!');
          const linkTarget = (isImage ? match[1] : match[3] || '').trim();
          const linkLabel = (isImage ? match[2] : match[4] || '').trim();

          // Add text before the match
          if (match.index > lastIndex) {
            const textToken = new state.Token('text', '', 0);
            textToken.content = tokens[i].content.slice(lastIndex, match.index);
            inlineTokens.push(textToken);
          }

          // data-URL embeds: ![[alt|data:image/…]] render as inline figures
          if (isImage && linkLabel.startsWith('data:image') && isImageUrl(linkLabel)) {
            const figureToken = new state.Token('html_inline', '', 0);
            const alt = linkTarget || 'image';
            figureToken.content =
              `<figure class="cm-embedded-image">` +
              `<img src="${escapeHtml(linkLabel)}" alt="${escapeHtml(alt)}" loading="lazy">` +
              (alt !== 'image' ? `<figcaption>${escapeHtml(alt)}</figcaption>` : '') +
              `</figure>`;
            inlineTokens.push(figureToken);
          } else if (isImage && isImageUrl(linkTarget)) {
            // Create image tokens
            const imgToken = new state.Token('image', 'img', 0);
            imgToken.attrSet('src', linkTarget);
            imgToken.attrSet('alt', linkLabel || '');
            imgToken.content = '';
            imgToken.children = [];
            inlineTokens.push(imgToken);
          } else {
            // Create link tokens — display text is the label, href is the target
            const displayText = linkLabel || linkTarget;
            const linkHref = options.wikilinksSearchPrefix
              ? `#&${options.wikilinksSearchPrefix}=${encodeURIComponent(linkTarget)}`
              : `/${encodeURIComponent(linkTarget)}.html`;

            const linkOpen = new state.Token('link_open', 'a', 1);
            linkOpen.attrSet('href', linkHref);
            linkOpen.attrSet('class', 'cm-wikilink-preview');
            inlineTokens.push(linkOpen);

            const text = new state.Token('text', '', 0);
            text.content = displayText;
            inlineTokens.push(text);

            const linkClose = new state.Token('link_close', 'a', -1);
            inlineTokens.push(linkClose);
          }

          lastIndex = wikilinkRegex.lastIndex;
        }

        // Add remaining text
        if (lastIndex < tokens[i].content.length) {
          const textToken = new state.Token('text', '', 0);
          textToken.content = tokens[i].content.slice(lastIndex);
          inlineTokens.push(textToken);
        }

        // Replace the original inline token with the new tokens
        tokens.splice(i, 1, ...inlineTokens);
      }
    }
  }

  md.core.ruler.push('wikilink', wikilinkRule);
}

export default wikilinksPlugin;
